import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Link, useLocation, useNavigate, useSearchParams } from '@remix-run/react';
import cx from 'classnames';

import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { HomePageLoaderData } from '~/routes';

export interface NotFoundProps {
  path: string;
}

const NotFound = ({ path }: NotFoundProps) => {
  const errorCode = '404';
  const { t } = useTranslation();
  const title = t(`errors.${errorCode}.title`);
  const description1 = t(`errors.${errorCode}.description`);
  const description2 = t(`errors.${errorCode}.description2`);

  const { homePage } = useTypedLoaderData<HomePageLoaderData>();
  const { general, headline, description } = homePage.main_section;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (errorCode === '404' && !searchParams.has('404')) {
      navigate(`/?${new URLSearchParams({ '404': pathname })}`);
    }
  }, [errorCode, pathname, searchParams, navigate]);

  return (
    <div
      className={cx('mx-auto flex w-full items-center justify-center px-4 py-24')}
      style={{ backgroundColor: general.bg_color }}
    >
      <div className={cx('mx-auto max-w-max')}>
        <main className="sm:flex">
          <p className="text-4xl font-extrabold sm:text-5xl" style={{ color: headline.color }}>
            {errorCode}
          </p>
          <div className="sm:ml-6">
            <div className={cx('sm:border-l sm:border-gray-200 sm:pl-6')}>
              <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl" style={{ color: headline.color }}>
                {title}
              </h1>

              <p className="mb-2 mt-2 text-base" style={{ color: description.color }}>
                {description1}:{' '}
                <Link to={path} className="font-mono underline">
                  {path}
                </Link>
              </p>
              <p className="mb-2 mt-2 text-base" style={{ color: description.color }}>
                {description2}
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default NotFound;
